import { CSSResultGroup, TemplateResult, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './tufts-tag-table.css.lit';
import tuftsHeadingCssLit from '../tufts-heading/tufts-heading.css.lit';
import '../tufts-heading/tufts-heading';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { SlotNGController } from '../../controllers/slot-ng-controller';
import { ResizeController } from '../../controllers/resize-controller';
import { ClassInfo, classMap } from 'lit/directives/class-map.js';

/**
 * The tuftsTagTable component - Simple component to render list of items into a table with a select number visable with rest of items hidden behind a button
 * @element tufts-tag-table
 *
 * @slot tag
 */
@customElement('tufts-tag-table')
export class tuftsTagTable extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles, tuftsHeadingCssLit];
  ngSlot = new SlotNGController(this);
  wasButtonClicked = false;
  standardVisableContent = 18;
  resizeController: ResizeController;

  // The resizeController is initialized here because verticalTriggerWidth is not set yet when constructor runs
  connectedCallback() {
    super.connectedCallback();
    this.resizeController = new ResizeController(this, {
      breakpoints: [this.verticalTriggerWidth],
    });
  }

  /**
   * Width at which layout changes from horizontal to vertical
   */
  @property({
    type: String,
    reflect: true,
    converter: Number,
    attribute: 'vertical-trigger-width',
  })
  verticalTriggerWidth = 768;

  /**
   * Display options
   * line-seperator
   * no-seperator
   * check-mark
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'display-options',
  })
  displayOptions = 'line-seperator';

  isMobile: boolean;

  /* Creates button if any tags exist */
  updated() {
    if (this.ngSlot.exist('tag') && !this.wasButtonClicked) {
      this.activateButton();
    }
  }

  contentTemplate(cardWrapperClasses: ClassInfo): TemplateResult {
    // Clones all slots with "tag" and moves to shadowDom
    const allContent = this.ngSlot.renderInShadow(
      'tag',
      '',
      [],
      false
    ) as HTMLElement[];
    //Sort items in alphabetical order
    allContent.sort((a, b) => a.innerText.localeCompare(b.innerText));
    //Breaking the list into the standard number for rendering
    const content = allContent.slice(0, this.standardVisableContent);
    // Variable for the content beyond the standard number to be hidden behind a button
    const hiddenContent = allContent.slice(
      this.standardVisableContent,
      allContent.length
    );
    // Number of hidden items to be displayed on the button
    const hiddenContentCount = allContent.length - content.length;
    // To ensure that a button does not have 1-4 items
    if (allContent.length <= this.standardVisableContent + 4) {
      return html`<div class=${classMap(cardWrapperClasses)}>
        ${allContent}
      </div>`;
    } else {
      return html`
        <div class=${classMap(cardWrapperClasses)}>${content}</div>
        ${this.wasButtonClicked
          ? html`<div class=${classMap(cardWrapperClasses)}>${allContent}</div>`
          : html` <div class="button-wrapper">
                <button type="button" class="show-more">
                  View <strong>${hiddenContentCount}</strong> more conditions
                </button>
              </div>
              <div
                hidden
                class="hidden-content ${this.isMobile ? 'mobile' : ''}"
              >
                <div class="class=${classMap(cardWrapperClasses)} extra-table">
                  ${hiddenContent}
                </div>
              </div>`}
      `;
    }
  }

  activateButton() {
    const button = this.renderRoot.querySelector('button');
    const hiddenContent: HTMLElement | null =
      this.renderRoot.querySelector('.hidden-content');
    if (button && hiddenContent) {
      hiddenContent.setAttribute('tabIndex', '-1');
      button.addEventListener('click', () => {
        button.setAttribute('hidden', '');
        hiddenContent.removeAttribute('hidden');
        hiddenContent.focus();
        this.wasButtonClicked = true;
      });
    }
  }

  render(): TemplateResult {
    this.isMobile = this.resizeController.currentBreakpointRange === 0;
    const cardWrapperClasses = {
      'tag-table': true,
      'mobile': this.isMobile,
      'has-border': this.displayOptions === 'line-seperator',
      'no-border': this.displayOptions === 'no-seperator',
      'check-mark': this.displayOptions === 'check-mark',
    };
    return html` ${this.contentTemplate(cardWrapperClasses)} `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-tag-table': tuftsTagTable;
  }
}
